/**
 * ISO 3166-1 alpha-2 to full country name mapping.
 *
 * THE canonical country catalogue for the app. Five rival hardcoded maps used to exist alongside
 * it (21, 21, 12, 5 and 5 entries); each rendered a bare ISO code for anything it had not been
 * taught, and they disagreed on labels — `AE` was 'UAE' in two of them and 'United Arab Emirates'
 * here. `country_of_incorporation` is a free ISO code off the backend's 249-row `places` table, so
 * a hand-kept subset drifts from the data by construction: SA and QA reached prod unmapped and
 * rendered as 'SA' and 'QA' on the dashboard. Add codes HERE, never in a component.
 */
export const COUNTRY_NAMES: Record<string, string> = {
  AD: 'Andorra',
  AE: 'United Arab Emirates',
  AT: 'Austria',
  AU: 'Australia',
  BB: 'Barbados',
  BE: 'Belgium',
  BG: 'Bulgaria',
  BH: 'Bahrain',
  BM: 'Bermuda',
  BR: 'Brazil',
  BS: 'Bahamas',
  CA: 'Canada',
  CH: 'Switzerland',
  CN: 'China',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DE: 'Germany',
  DK: 'Denmark',
  EG: 'Egypt',
  ES: 'Spain',
  FI: 'Finland',
  FR: 'France',
  GB: 'United Kingdom',
  GG: 'Guernsey',
  GI: 'Gibraltar',
  GR: 'Greece',
  HK: 'Hong Kong',
  HR: 'Croatia',
  HU: 'Hungary',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IM: 'Isle of Man',
  IN: 'India',
  IT: 'Italy',
  JE: 'Jersey',
  JP: 'Japan',
  KE: 'Kenya',
  KR: 'South Korea',
  KW: 'Kuwait',
  KY: 'Cayman Islands',
  LI: 'Liechtenstein',
  LU: 'Luxembourg',
  MC: 'Monaco',
  MT: 'Malta',
  MU: 'Mauritius',
  MX: 'Mexico',
  MY: 'Malaysia',
  NG: 'Nigeria',
  NL: 'Netherlands',
  NO: 'Norway',
  NZ: 'New Zealand',
  PH: 'Philippines',
  PK: 'Pakistan',
  PL: 'Poland',
  PT: 'Portugal',
  QA: 'Qatar',
  RO: 'Romania',
  SA: 'Saudi Arabia',
  SE: 'Sweden',
  SG: 'Singapore',
  SK: 'Slovakia',
  TH: 'Thailand',
  TR: 'Turkey',
  TW: 'Taiwan',
  US: 'United States',
  VG: 'British Virgin Islands',
  ZA: 'South Africa',
  ZW: 'Zimbabwe',
}

/**
 * Returns the full country name for a given ISO 3166-1 alpha-2 code.
 * Falls back to the code itself if not found.
 */
export function getCountryName(code: string | null | undefined): string | null | undefined {
  if (!code) return code
  return COUNTRY_NAMES[code.toUpperCase()] ?? code
}

/**
 * Flag emoji for an ISO 3166-1 alpha-2 code, DERIVED rather than tabulated.
 *
 * A flag emoji is just the two letters as Unicode regional indicators (U+1F1E6 is 'A'), so every
 * valid alpha-2 code produces one without a lookup table. The 21-entry `COUNTRY_FLAGS` map this
 * replaces had the same defect as the name maps: a country it had not been taught silently lost
 * its flag. Returns null for anything that is not two ASCII letters, so callers can omit the glyph
 * rather than print a stray box.
 */
export function getCountryFlag(code: string | null | undefined): string | null {
  if (!code) return null
  const upper = code.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(upper)) return null
  return String.fromCodePoint(
    ...[...upper].map((ch) => 0x1f1e6 + (ch.charCodeAt(0) - 65)),
  )
}
